import { Button } from "../components/ui/button";
import { X, Printer, HandHeart } from "lucide-react";

export default function PrintPreview({ 
  isOpen, 
  onClose, 
  prayers, 
  title, 
  subtitle,
  showGroupColumn = false,
  groups = [],
  sortMode = "newest"
}) {
  if (!isOpen) return null;

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  const getGroupName = (groupId) => {
    if (!groupId) return "Personal";
    const group = groups.find(g => g.id === groupId);
    return group ? group.name : "Unknown";
  };

  const handlePrint = () => {
    const printContent = document.querySelector('.print-content');
    if (!printContent) return;

    // Clone print content and append directly to body
    const clone = printContent.cloneNode(true);
    clone.id = 'print-clone';

    // Create print-only styles
    const style = document.createElement('style');
    style.id = 'print-clone-style';
    style.textContent = `
      @media print {
        body > *:not(#print-clone):not(#print-clone-style) {
          display: none !important;
        }
        #print-clone {
          display: block !important;
          visibility: visible !important;
          position: static !important;
          width: 100% !important;
          padding: 0.5in !important;
          margin: 0 !important;
        }
        #print-clone * {
          visibility: visible !important;
        }
        #print-clone table {
          width: 100% !important;
          border-collapse: collapse !important;
          table-layout: fixed !important;
        }
        #print-clone th,
        #print-clone td {
          border: 1px solid #ccc !important;
          padding: 6px 8px !important;
          text-align: left !important;
          font-size: 11px !important;
          word-wrap: break-word !important;
          overflow-wrap: break-word !important;
        }
        #print-clone th {
          background-color: #f0f0f0 !important;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
          font-weight: 600 !important;
        }
        #print-clone tr {
          page-break-inside: avoid !important;
          break-inside: avoid !important;
        }
        #print-clone .print-section {
          page-break-inside: auto !important;
        }
        #print-clone .print-section h2 {
          page-break-after: avoid !important;
          break-after: avoid !important;
        }
        #print-clone .notes-row td {
          background-color: #fafafa !important;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        #print-clone h1, #print-clone h2 {
          color: #000 !important;
        }
      }
      @media screen {
        #print-clone, #print-clone-style {
          display: none !important;
        }
      }
    `;

    document.body.appendChild(style);
    document.body.appendChild(clone);

    window.print();

    // Cleanup after print dialog closes
    setTimeout(() => {
      const existingClone = document.getElementById('print-clone');
      const existingStyle = document.getElementById('print-clone-style');
      if (existingClone) document.body.removeChild(existingClone);
      if (existingStyle) document.body.removeChild(existingStyle);
    }, 500);
  };

  const activePrayers = prayers.filter(p => !p.is_answered);
  const answeredPrayers = prayers.filter(p => p.is_answered);

  // Group prayers by requester for "requester" sort mode
  const groupByRequester = (prayerList) => {
    const grouped = {};
    prayerList.forEach((prayer) => {
      const key = prayer.requested_by || "Unspecified";
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(prayer);
    });
    return Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b));
  };

  const sortLabel = sortMode === "oldest" ? "Sorted: Oldest First" 
    : sortMode === "requester" ? "Grouped by: Who Requested" 
    : "Sorted: Newest First";

  return (
    <div className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center p-4" data-testid="print-preview-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col">
        
        {/* Header toolbar */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="font-serif text-xl font-semibold text-stone-dark">Print Preview</h2>
          <div className="flex items-center gap-2">
            <Button 
              className="bg-sage hover:bg-sage-dark text-white"
              onClick={handlePrint}
              data-testid="print-btn"
            >
              <Printer className="w-4 h-4 mr-2" />
              Print
            </Button>
            <Button variant="ghost" size="sm" onClick={onClose} data-testid="close-print-preview">
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Scrollable preview area */}
        <div className="flex-1 overflow-auto p-6">
          <div className="print-content">
            {/* Print Header */}
            <div className="text-center mb-6">
              <h1 className="text-2xl font-serif font-bold text-sage">
                {title}
              </h1>
              {subtitle && (
                <p className="text-stone-dark/60 mt-1">{subtitle}</p>
              )}
              <p className="text-sm text-stone-dark/50 mt-2">
                Printed on {new Date().toLocaleDateString("en-US", { 
                  weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' 
                })}
                {sortMode !== "newest" && (
                  <span className="ml-2">| {sortLabel}</span>
                )}
              </p>
            </div>

            {/* Active Prayers */}
            {activePrayers.length > 0 && (
              <div className="mb-8 print-section">
                <h2 className="text-lg font-semibold text-sage mb-3 pb-2 border-b-2 border-sage">
                  Active Prayers ({activePrayers.length})
                </h2>
                {sortMode === "requester" ? (
                  groupByRequester(activePrayers).map(([requester, prayerGroup]) => (
                    <div key={requester} className="mb-4">
                      <h3 className="text-sm font-semibold text-stone-dark bg-stone/30 px-3 py-1.5 rounded mb-2">
                        {requester} ({prayerGroup.length})
                      </h3>
                      <PrayerTable 
                        prayers={prayerGroup}
                        formatDate={formatDate}
                        showGroupColumn={showGroupColumn}
                        getGroupName={getGroupName}
                      />
                    </div>
                  ))
                ) : (
                  <PrayerTable 
                    prayers={activePrayers}
                    formatDate={formatDate}
                    showGroupColumn={showGroupColumn}
                    getGroupName={getGroupName}
                  />
                )}
              </div>
            )}

            {/* Answered Prayers */}
            {answeredPrayers.length > 0 && (
              <div className="mb-8 print-section">
                <h2 className="text-lg font-semibold text-gold mb-3 pb-2 border-b-2 border-gold">
                  Answered Prayers ({answeredPrayers.length})
                </h2>
                {sortMode === "requester" ? (
                  groupByRequester(answeredPrayers).map(([requester, prayerGroup]) => (
                    <div key={requester} className="mb-4">
                      <h3 className="text-sm font-semibold text-stone-dark bg-stone/30 px-3 py-1.5 rounded mb-2">
                        {requester} ({prayerGroup.length})
                      </h3>
                      <PrayerTable 
                        prayers={prayerGroup}
                        formatDate={formatDate}
                        showGroupColumn={showGroupColumn}
                        getGroupName={getGroupName}
                        showAnswer={true}
                      />
                    </div>
                  ))
                ) : (
                  <PrayerTable 
                    prayers={answeredPrayers}
                    formatDate={formatDate}
                    showGroupColumn={showGroupColumn}
                    getGroupName={getGroupName}
                    showAnswer={true}
                  />
                )}
              </div>
            )}

            {prayers.length === 0 && (
              <p className="text-center text-stone-dark/60 py-8">No prayers to display</p>
            )}

            <div className="text-center text-xs text-gray-400 mt-8 pt-4 border-t">
              Prayer Tracker - Keep Your Prayers Close to Heart
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function PrayerTable({ 
  prayers, 
  formatDate, 
  showGroupColumn,
  getGroupName,
  showAnswer = false 
}) {
  const colCount = 2 + (showGroupColumn ? 1 : 0) + (showAnswer ? 1 : 0);

  return (
    <table className="w-full border-collapse text-sm" data-testid="prayer-print-table">
      <thead>
        <tr className="bg-stone/50">
          <th className="border border-stone p-2 text-left font-medium text-stone-dark">Prayer Request</th>
          {showGroupColumn && (
            <th className="border border-stone p-2 text-left font-medium text-stone-dark" style={{width: '120px'}}>Group</th>
          )}
          <th className="border border-stone p-2 text-left font-medium text-stone-dark" style={{width: '120px'}}>Requested By</th>
          <th className="border border-stone p-2 text-left font-medium text-stone-dark" style={{width: '100px'}}>Date</th>
          {showAnswer && (
            <th className="border border-stone p-2 text-left font-medium text-stone-dark" style={{width: '100px'}}>Answered</th>
          )}
        </tr>
      </thead>
      <tbody>
        {prayers.map((prayer) => (
          <PrayerRow
            key={prayer.id}
            prayer={prayer}
            formatDate={formatDate}
            showGroupColumn={showGroupColumn}
            getGroupName={getGroupName}
            showAnswer={showAnswer}
            colCount={colCount}
          />
        ))}
      </tbody>
    </table>
  );
}

function PrayerRow({ prayer, formatDate, showGroupColumn, getGroupName, showAnswer, colCount }) {
  const hasDetails = prayer.notes || prayer.result;
  const prayerCount = prayer.prayer_count || 0;

  return (
    <>
      <tr>
        <td className="border border-stone p-2 text-stone-dark">
          {prayer.request}
          {prayerCount >= 1 && (
            <div
              className="flex items-center gap-1 text-xs text-sage mt-1"
              data-testid={`print-prayer-count-${prayer.id}`}
            >
              <HandHeart className="w-3 h-3" />
              {prayerCount} {prayerCount === 1 ? "prayer" : "prayers"}
            </div>
          )}
        </td>
        {showGroupColumn && (
          <td className="border border-stone p-2 text-stone-dark/70">
            {getGroupName(prayer.group_id)}
          </td>
        )}
        <td className="border border-stone p-2 text-stone-dark/70">
          {prayer.requested_by || prayer.requestor_name || "-"}
        </td>
        <td className="border border-stone p-2 text-stone-dark/70 whitespace-nowrap">
          {formatDate(prayer.date_requested || prayer.created_at)}
        </td>
        {showAnswer && (
          <td className="border border-stone p-2 text-stone-dark/70 whitespace-nowrap">
            {formatDate(prayer.date_answered || prayer.answered_at)}
          </td>
        )}
      </tr>
      {hasDetails && (
        <tr className="notes-row">
          <td colSpan={colCount} className="border border-stone p-2 bg-stone/10">
            {prayer.notes && (
              <div className="mb-1">
                <span className="font-medium text-stone-dark text-xs">Notes: </span>
                <span className="text-stone-dark/70 text-xs italic">"{prayer.notes}"</span>
              </div>
            )}
            {prayer.result && (
              <div>
                <span className="font-medium text-sage text-xs">Result: </span>
                <span className="text-stone-dark/70 text-xs">{prayer.result}</span>
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}
